/**
 * Centralized utility for accessing the SDK's root container element (.atrius-map)
 * and computing container-relative dimensions/coordinates.
 *
 * Use these helpers instead of window.innerWidth / window.innerHeight so that
 * the SDK works correctly when embedded without an iframe.
 */

const MAP_ELEMENT_SELECTOR = '.atrius-map';

let cachedElement: Element | null = null;

/**
 * Returns the SDK root container element (.atrius-map).
 * Caches the result after the first successful lookup.
 * @returns {HTMLElement|null}
 */
export const getMapElement = () => {
  if (cachedElement && cachedElement.isConnected) {
    return cachedElement;
  }
  cachedElement = document.querySelector(MAP_ELEMENT_SELECTOR);
  return cachedElement;
};

/**
 * Returns the bounding rect of the SDK container, or a window-sized fallback.
 * The returned object has: width, height, top, left, right, bottom.
 * @returns {{ width: number, height: number, top: number, left: number, right: number, bottom: number }}
 */
export const getContainerRect = () => {
  const el = getMapElement();
  if (el) {
    return el.getBoundingClientRect();
  }
  return {
    top: 0,
    left: 0,
    right: window.innerWidth,
    bottom: window.innerHeight,
    width: window.innerWidth,
    height: window.innerHeight,
  };
};

/**
 * Returns the width of the SDK container (falls back to window.innerWidth).
 * Uses clientWidth for consistency with original implementations.
 * @returns {number}
 */
export const getContainerWidth = () => {
  const el = getMapElement();
  return el?.clientWidth || window.innerWidth;
};

/**
 * Returns the height of the SDK container (falls back to window.innerHeight).
 * Uses clientHeight for consistency with original implementations.
 * @returns {number}
 */
export const getContainerHeight = () => {
  const el = getMapElement();
  return el?.clientHeight || window.innerHeight;
};

/**
 * Converts a viewport-relative DOMRect (from getBoundingClientRect()) to
 * coordinates relative to the SDK container.
 * @param {DOMRect} rect - Result of element.getBoundingClientRect()
 * @returns {{ top: number, left: number, right: number, bottom: number, width: number, height: number }}
 */
export const toContainerCoords = (rect: DOMRect) => {
  const container = getContainerRect();
  return {
    top: rect.top - container.top,
    left: rect.left - container.left,
    right: rect.right - container.left,
    bottom: rect.bottom - container.top,
    width: rect.width,
    height: rect.height,
  };
};

/** @internal — exposed for testing only */
export const _resetCache = () => {
  cachedElement = null;
};
